"use client";

import TextHeaderSmall from "@/components/typography/TextHeaderSmall";
import React, { useState, useRef } from "react";
import getApartmentsData from "@/hooks/data/getApartmentsData";
import TextSubHeader from "@/components/typography/TextSubHeader";
import Button from "@/components/ui/Button";
import { useLeadModal } from "@/contexts/leadModal";
import Image from "next/image";
import { money } from "@/utils/formatters";
import Modal from "@/components/ui/Modal";
import { createPortal } from "react-dom";
import { SwiperSlide } from "swiper/react";
import SwiperCarousel from "@/components/plugins/swiper/SwiperCarousel";
import TextContent from "@/components/typography/TextContent";
import IconFloorPlan from "@/components/icons/IconFloorPlan";
import TextDisclaimer from "@/components/typography/TextDisclaimer";

export default function ProfitApartments() {
  const [disclaimerOpened, setDisclaimerOpened] = useState<boolean>(false);
  const swiperRef = useRef(null);
  const apartments = getApartmentsData().sort(
    (a, b) =>
      a.inside.reduce((sum, inside) => sum + inside.metric, 0) -
      b.inside.reduce((sum, inside) => sum + inside.metric, 0),
  );
  const leadModal = useLeadModal();

  const handleModalOpen = (e: React.MouseEvent<HTMLButtonElement>): void => {
    e.preventDefault();
    setDisclaimerOpened(true);
  };

  const handleModalClose = (): void => {
    setDisclaimerOpened(false);
  };

  const disclaimerText = `Inwestycja polega na zakupie indywidualnego (samodzielnego) lokalu
      apartamentowego (niemieszkalnego), tj. pełnej własności ww. lokalu
      wraz z prawami z nim związanymi, dla którego to lokalu prowadzona
      będzie odrębna księga wieczysta. Właściciel apartamentu uprawniony
      jest do zawarcia umowy najmu lokalu z podmiotem wskazanym przez
      Sprzedającego, tj. Najemcą. Umowa najmu zawierana jest na okres 10
      lat z możliwością jej przedłużenia na kolejne 5 lat. Podpisując
      umowę najmu z Najemcą Właściciel otrzymuje czynsz stanowiący 70%
      (słownie: siedemdziesiąt procent) przychodu netto (po odjęciu podat
      ku VAT) Najemcy z tytułu podnajmu Lokalu, pomniejszonego o koszty:
      obsługi płatności i transakcji internetowych, marketingu, opłat
      franczyzowych, wynagrodzenia sieci hotelowej, prowizji podmiotów
      trzecich, takich jak: por tale rezer wacyjne, biura podróży,
      pośrednicy tur ystyczni i tym podobnych, z któr ymi Najemca zawrze
      umowy współpracy. Właściciel pokr ywa koszty eksploatacyjne związane
      z utrzymaniem i zarządzaniem lokalem oraz nieruchomością wspólną (w
      tym niezbędnych mediów), ubezpieczania przedmiotu umowy najmu, opłat
      publicznoprawnych, tj. w szczególności podat ku od nieruchomości,
      niezbędnych remontów i robót adaptacyjnych oraz jednorazową opłatę
      za przygotowanie i wyposażenie Lokalu. Wszyst kie przychody z najmu
      są opodat kowane zgodnie z obowiązującymi przepisami. Umowa najmu
      umożliwia również Właścicielowi korzystanie z apartamentu w
      terminach i na warunkach (obejmujących także ponoszenie przez
      Właściciela stosownych opłat związanych z korzystaniem z lokalu za
      pobyt) określonych w umowie najmu. Zabezpieczenie przychodu minimum
      na poziomie 8% w skali roku dotyczy lokali nabywanych wraz z ich
      wykończeniem i wyposażeniem w standardzie określonym w załącznikach
      do Umowy Przedwstępnej (zwanych dalej także jako „Lokale”), a także
      ewentualnie wraz z miejscem parkingowym. Na podstawie zawar tej
      Umowy Najmu właściciel lokalu, tj. wynajmujący, otrzymuje tzw.
      podziałowy czynsz najmu wyliczony zgodnie z zasadami opisanymi w
      obowiązującej Umowie Najmu, przy czym minimalna war tość ww. czynszu
      w danym roku kalendarzowym należna Wynajmującemu stanowić będzie
      iloczyn 8% z sumy ceny netto lokalu objętego Umową Najmu wraz z ceną
      netto jego wyposażenia i ceną netto ewentualnego miejsca
      parkingowego lub postojowego. Innymi słowy, jeżeli wysokość
      obliczonego zgodnie ustalonymi w obowiązującej Umowie Najmu Czynszu
      Najmu w danym roku kalendarzowym będzie niższa niż ww. Czynsz
      Minimalny, wówczas Najemca wypłaci na rzecz Wynajmującego kwotę
      stanowiącą różnicę pomiędzy Czynszem Minimalnym, a ww. wysokością
      Czynszu Najmu naliczonego w danym roku kalendarzowym zgodnie z
      zasadami określonymi w obowiązującej Umowie Najmu. Każda inwestycja
      wiąże się z r yzykiem. Niniejsze informacje nie stanowią ofer ty w
      rozumienie Ar t. 66 § 1 Kodeksu Cywilnego i nie stanowią gwarancji
      uzyskania prognozowanych wyników oraz świadczeń w przyszłości, a
      szczegóły informacje nt. sposobu wyliczenia ww. podziałowego czynszu
      najmu) dostępne są u doradcy w Biurze Sprzedaży, a także częściowo
      na stronie www Inwestycji.`;

  return (
    <div className={"mb-10 lg:mb-24"}>
      <div
        className="relative z-10 mb-10 max-w-screen-xl px-5 lg:m-auto lg:mb-16 lg:px-0"
        id={"profit-apartments"}
      >
        <TextHeaderSmall className="pt-10 lg:pt-16 lg:text-center">
          Zobacz przykładowy apartament
        </TextHeaderSmall>
      </div>
      <div className={"relative"}>
        <SwiperCarousel
          className={"!max-w-screen-xl lg:!pr-0"}
          paginationClassName={"!bg-[#34EEFF]/30"}
          scrollbar={false}
          pagination={{}}
          breakpoints={{
            0: {
              slidesPerView: 1,
              spaceBetween: 20,
              slidesOffsetAfter: -28,
            },
            1024: {
              slidesPerView: 1,
              spaceBetween: 40,
              slidesOffsetAfter: 0,
            },
          }}
        >
          {apartments.map((apartment) => (
            <SwiperSlide key={apartment.id} className={"h-auto"}>
              <div
                className={
                  "flex h-full flex-col justify-between gap-7 rounded-3xl border-2 border-[#DCDCE8] px-5 py-10 shadow-[0_10px_0_0_rgba(0,0,0,0.05)] lg:grid lg:grid-cols-2 lg:gap-40 lg:p-12"
                }
              >
                <div
                  className={
                    "divide-gray-[#dcdce8] flex flex-1 flex-col space-y-5 divide-y lg:space-y-7"
                  }
                >
                  <div>
                    <div className={"flex items-baseline justify-between"}>
                      <TextContent className={"lg:font-semibold"}>
                        Numer apartamentu
                      </TextContent>
                      <TextHeaderSmall className={"!mb-0 text-right"}>
                        {apartment.number}
                      </TextHeaderSmall>
                    </div>
                    <div className={"flex items-baseline justify-between"}>
                      <TextContent className={"lg:font-semibold"}>
                        Piętro
                      </TextContent>
                      <TextSubHeader className={"!font-bold"}>
                        {apartment.floor}
                      </TextSubHeader>
                    </div>
                  </div>
                  <div className={"space-y-1 pt-5 lg:space-y-2.5 lg:pt-7"}>
                    {[...apartment.inside, ...apartment.outside].map(
                      (item, itemIndex) => (
                        <div
                          key={itemIndex}
                          className={"flex items-baseline justify-between"}
                        >
                          <TextContent>{item.name}</TextContent>
                          <TextContent>
                            {item.metric} m<sup>2</sup>
                          </TextContent>
                        </div>
                      ),
                    )}
                    <div className={"flex items-baseline justify-between"}>
                      <TextContent className={"font-semibold"}>
                        Powierzchnia lokalu
                      </TextContent>
                      <TextContent>
                        {apartment.inside
                          .reduce((sum, inside) => sum + inside.metric, 0)
                          .toFixed(2)}{" "}
                        m<sup>2</sup>
                      </TextContent>
                    </div>
                  </div>
                  <div
                    className={
                      "flex flex-1 flex-col justify-end space-y-4 pt-5 lg:space-y-7 lg:pt-7"
                    }
                  >
                    <div className={"flex items-end justify-between"}>
                      <TextContent>
                        <div className={"font-semibold"}>Cena netto</div>
                        <div>z wykończeniem i wyposażeniem</div>
                      </TextContent>
                      <TextSubHeader className={"whitespace-nowrap !font-bold"}>
                        {money(apartment.price)} zł
                      </TextSubHeader>
                    </div>
                    <div className={"flex items-center justify-between"}>
                      <TextContent className={"!font-bold"}>
                        Prognozowany
                        <br /> przychód 12,1%
                      </TextContent>
                      <div className={"relative"}>
                        <svg
                          className={
                            "absolute left-1/2 top-1/2 -z-10 w-[120%] -translate-x-1/2 -translate-y-1/2"
                          }
                          width="259"
                          height="38"
                          viewBox="0 0 259 38"
                          fill="none"
                          xmlns="http://www.w3.org/2000/svg"
                        >
                          <g clipPath="url(#clip0_1678_521)">
                            <path
                              d="M226.807 20.6774C226.45 21.0062 224.019 21.1416 225.82 22.1757C226.896 22.7942 229.625 21.2473 230.275 22.7879L222.588 22.5912L218.39 23.2108C220.065 23.8463 221.501 23.1833 223.016 23.2394C227.322 23.398 231.691 23.9901 236.058 24.32C236.998 24.3908 239.159 23.5788 239.683 24.9026C238.099 25.3213 231.863 24.2111 230.768 24.6869C230.535 24.7873 230.137 25.2303 230.275 25.3255L232.608 25.4693L234.236 26.5933C233.177 26.6472 232.083 26.4992 231.024 26.6028C226.843 27.0109 222.71 27.6295 218.762 27.7257C216.118 27.7912 214.639 27.7257 212.205 26.589L213.435 27.861C211.771 27.5533 210.127 28.1655 208.64 28.2469C206.802 28.3474 204.431 27.9139 202.786 27.9456C165.588 28.6731 128.677 30.0254 91.7983 32.4573C89.4675 32.6106 85.3742 31.6886 83.4161 33.3613C82.3931 30.8671 79.3947 33.6532 78.965 33.6785C78.5786 33.7007 78.0113 33.0875 77.4837 33.0864C77.002 33.0854 76.2341 33.6754 75.439 33.7462C72.6647 33.9936 69.888 33.853 67.1336 34.2379L66.3385 33.3656L65.8307 35.051L65.0331 34.2019C60.1955 35.0161 55.3047 35.4189 50.3507 35.1144L48.2502 36.3219L47.7548 35.4528C47.0649 35.4359 46.4692 36.0914 45.7075 36.1728C36.5092 37.1614 26.4142 36.9542 17.1093 37.9999C16.1755 37.9312 13.9388 37.1572 13.1573 36.6867C10.3892 35.0203 10.7546 33.6352 9.47523 31.7933C6.12261 26.9676 2.85916 23.3398 1.18843 17.7274C-0.607396 11.6984 -1.48054 8.21233 6.98461 7.02282C17.601 5.5309 29.9365 6.05112 40.8192 5.45054C106.595 1.82067 172.451 -2.15707 237.882 1.3713L258.999 4.61207C258.941 6.6538 255.688 5.68316 254.049 5.883L255.532 7.1423C253.99 7.77671 252.281 7.73336 250.866 8.02413C249.768 8.2504 249.28 8.9123 247.839 8.88904C247.162 8.87846 246.118 6.57873 246.364 8.84463L243.149 8.84251L243.65 9.67993L240.677 9.68733C241.125 10.843 243.735 9.75818 244.422 9.73174C244.656 9.72223 244.578 10.4581 244.96 10.5279C246.213 10.7584 248.865 10.0373 249.589 11.3749H240.179C240.37 12.5263 243.162 11.5536 242.654 13.0634L239.192 13.4843C240.34 13.0275 240.849 14.3301 240.969 14.3291C241.485 14.3238 242.048 13.8649 242.802 13.9981C245.88 14.5437 250.036 15.9088 243.65 16.0293L246.124 16.871H234.237L236.71 18.1366L232.741 17.9389L232.259 19.4065L236.216 19.8273L227.8 19.8358L230.767 20.6816C230.079 21.9621 228.207 21.4799 227.303 20.6774C227.303 20.3517 227.482 19.8241 227.055 19.8273C226.535 19.8167 226.943 20.5505 226.807 20.6774ZM235.721 9.68205C235.713 8.13198 234.248 8.13198 234.239 9.68205H235.721ZM231.262 12.6479H230.276V13.0624H231.262V12.6479ZM236.216 12.6479L232.754 12.853C233.434 13.8004 235.807 13.6577 236.216 12.6479ZM238.692 12.6458L236.713 12.6479L237.712 14.3323H238.691L238.692 12.6458ZM243.149 15.6053H242.163V16.447H243.149V15.6053ZM210.46 19.4128C209.412 19.6465 206.581 18.6832 207 20.2534C207.894 19.5027 210.099 20.3073 210.46 19.4128ZM217.393 19.8368L211.949 19.4128C212.136 20.1815 212.922 20.1984 213.664 20.2693C214.364 20.3369 217.101 20.5696 217.393 19.8368ZM220.861 19.8358C219.699 19.9573 218.294 19.0068 217.892 20.2502H220.861V19.8358ZM223.832 20.2597C223.758 20.2005 222.025 19.7702 221.858 19.8347C221.721 20.448 223.081 20.5632 223.832 20.6732C223.808 20.5368 223.9 20.3137 223.832 20.2597ZM225.318 19.8358H224.333V20.2502H225.318V19.8358ZM220.368 21.942L219.625 21.1088L218.884 21.7369L220.368 21.942ZM223.834 21.5275H222.352V21.942H223.834V21.5275ZM206.002 22.7963L204.524 22.3681L204.522 23.2119L206.003 23.2097L206.002 22.7963ZM217.395 22.7963H214.428V23.2108H217.395V22.7963Z"
                              fill="#34EEFF"
                            />
                          </g>
                          <defs>
                            <clipPath id="clip0_1678_521">
                              <rect width="259" height="38" fill="white" />
                            </clipPath>
                          </defs>
                        </svg>
                        <div
                          className={
                            "relative z-10 whitespace-nowrap text-[34px] font-bold lg:text-[50px]"
                          }
                        >
                          {money(apartment.yearly_profit)} zł
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
                <div className={"lg:flex lg:flex-col"}>
                  <div className={"relative mb-7 lg:flex-1"}>
                    <Image
                      src={apartment.imageDraw}
                      alt={apartment.name}
                      className={
                        "h-full w-full object-contain lg:absolute lg:inset-0"
                      }
                    />
                  </div>
                  <div
                    className={
                      "flex flex-col items-center space-y-5 lg:flex-row lg:space-x-10 lg:space-y-0"
                    }
                  >
                    <a
                      className="flex cursor-pointer items-center"
                      title={"Pobierz rzut apartamentu"}
                      href={apartment.pdfFile}
                      target={"_blank"}
                    >
                      <div className="mr-3">
                        <IconFloorPlan className="w-8" />
                      </div>
                      <div className="text-[16px]/[24px] font-semibold lg:text-[18px]/[24px]">
                        <div className="mb-2">Pobierz rzut</div>
                        <div className="h-0.5 w-full bg-black"></div>
                      </div>
                    </a>
                    <Button
                      className="w-full lg:flex-1"
                      theme="default"
                      onClick={() => {
                        leadModal.setFlow({
                          flow: "apartment",
                          apartmentId: apartment.id,
                        });
                      }}
                    >
                      Zapytaj o ofertę
                    </Button>
                  </div>
                </div>
              </div>
            </SwiperSlide>
          ))}
        </SwiperCarousel>
        <div className="relative z-10 mb-10 mt-7 max-w-screen-xl px-5 lg:mx-auto lg:mb-16 lg:px-0">
          <div className={"lg:absolute lg:-bottom-2 lg:right-0 lg:w-96"}>
            <TextDisclaimer>
              {disclaimerText.substring(0, 250)}...{" "}
              <button
                type={"button"}
                className={"font-bold"}
                onClick={handleModalOpen}
              >
                Czytaj więcej
              </button>
            </TextDisclaimer>
          </div>
        </div>
      </div>
      {disclaimerOpened &&
        createPortal(
          <Modal isOpen={true} onClose={handleModalClose}>
            {disclaimerText}
          </Modal>,
          document.body,
          "formDisclaimer",
        )}
    </div>
  );
}
